<template>
  <div class="horizon-filter" data-testid="horizon-filter">
    <img class="horizon-filter__icon" src="/img/icons/FilterIcon.svg" alt="Filter Icon" data-testid="horizon-filter__icon"/>
    <v-menu
      v-for="chip in toolbarFilterChips"
      :key="chip.key"
      :value="openMenuKey === chip.key"
      offset-y
      :close-on-content-click="false"
      transition="slide-y-transition"
      content-class="horizon-filter__menu-content"
      @input="onMenuToggle(chip.key, $event)"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          outlined
          depressed
          class="horizon-filter__btn"
          :class="{ 'horizon-filter__btn--active': isActive(chip.key) }"
          data-testid="horizon-filter__chip"
        >
          <span class="horizon-filter__btn-label">
            {{ chip.label }}
            <span v-if="activeCount(chip.key)" class="horizon-filter__btn-count">
              ({{ activeCount(chip.key) }})
            </span>
          </span>
          <v-icon small class="horizon-filter__btn-chevron">mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-card class="horizon-filter__card" data-testid="horizon-filter__menu">
        <div class="horizon-filter__search" data-testid="horizon-filter__search">
          <lb-string
            label=""
            placeholder="Search"
            v-model="searchTerms[chip.key]"
            width="100%"
            :isClearable="true"
            appendiconinner="mdi-magnify"
            hidedetails
            radius="8px"
            :data-testid="`horizon-filter__search-${chip.key}`"
          />
        </div>

        <div
          v-if="filteredOptions(chip.key).length > 0"
          class="horizon-filter__select-all"
          data-testid="horizon-filter__select-all"
        >
          <v-btn
            text
            small
            color="primary"
            class="horizon-filter__select-all-btn text-none px-2"
            @click.stop="toggleSelectAll(chip.key)"
            :data-testid="`horizon-filter__select-all-${chip.key}`"
          >
            <v-icon small class="mr-1">{{ selectAllIcon(chip.key) }}</v-icon>
            <span>{{ selectAllLabel(chip.key) }}</span>
          </v-btn>
        </div>

        <div class="horizon-filter__list" data-testid="horizon-filter__list">
          <div
            v-for="option in filteredOptions(chip.key)"
            :key="`${chip.key}-${option.value}`"
            class="horizon-filter__item"
            @click="togglePendingOption(chip.key, option.value)"
            data-testid="horizon-filter__option"
          >
            <v-checkbox
              dense
              hide-details
              :ripple="false"
              class="horizon-filter__checkbox pa-0 ma-0"
              color="primary"
              :input-value="isPendingSelected(chip.key, option.value)"
              @click.stop
              @change="togglePendingOption(chip.key, option.value)"
            />
            <span class="horizon-filter__item-label">{{ option.label }}</span>
          </div>
          <div
            v-if="filteredOptions(chip.key).length === 0"
            class="horizon-filter__empty"
            data-testid="horizon-filter__empty"
          >
            No items found
          </div>
        </div>

        <v-divider />

        <div class="horizon-filter__actions" data-testid="horizon-filter__actions">
          <v-btn
            outlined
            class="horizon-filter__clear-btn text-none"
            @click="clearPending(chip.key)"
            data-testid="horizon-filter__clear"
          >
            Clear
          </v-btn>
          <v-btn
            depressed
            class="horizon-filter__apply-btn text-none white--text"
            @click="applyFilter(chip.key)"
            data-testid="horizon-filter__apply"
          >
            Apply
          </v-btn>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import './HorizonFilter.scss';
import { HORIZON_FILTER_CHIPS, HORIZON_FILTER_KEYS } from '../../../utils/horizonConstants';

const TOOLBAR_FILTER_ORDER = [
  HORIZON_FILTER_KEYS.LOCATION,
  HORIZON_FILTER_KEYS.USER,
  HORIZON_FILTER_KEYS.SUBCATEGORY,
  HORIZON_FILTER_KEYS.PRIORITY,
];

/** Multi-select filter chip menus — pending state until Apply (Level A options from HorizonLanding). */
export default {
  name: 'HorizonFilter',
  props: {
    horizonFilters: {
      type: Object,
      default: () => ({}),
    },
    activeFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      openMenuKey: null,
      pendingFilters: {},
      searchTerms: {},
    };
  },
  computed: {
    /** Filter chips in toolbar display order (location, user, subcategory, priority). */
    toolbarFilterChips() {
      const chipMap = HORIZON_FILTER_CHIPS.reduce((acc, chip) => {
        acc[chip.key] = chip;
        return acc;
      }, {});
      return TOOLBAR_FILTER_ORDER
        .map((key) => chipMap[key])
        .filter(Boolean);
    },
  },
  methods: {
    /** Opens/closes a chip menu and seeds pending selection from active filters. */
    onMenuToggle(chipKey, isOpen) {
      if (isOpen) {
        this.openMenuKey = chipKey;
        this.$set(this.pendingFilters, chipKey, this.getInitialPendingValues(chipKey));
        this.$set(this.searchTerms, chipKey, '');
      } else if (this.openMenuKey === chipKey) {
        this.openMenuKey = null;
      }
    },
    /** All option values for a filter chip (used for select-all logic). */
    allOptionValues(chipKey) {
      return (this.horizonFilters[chipKey] || []).map((option) => option.value);
    },
    /** Initial pending values when opening a menu (active filters or all options). */
    getInitialPendingValues(chipKey) {
      const active = this.activeFilters[chipKey];
      if (active?.length) {
        return [...active];
      }
      return this.allOptionValues(chipKey);
    },
    /** Options visible in the chip menu after search-term filtering. */
    filteredOptions(chipKey) {
      const options = this.horizonFilters[chipKey] || [];
      const term = (this.searchTerms[chipKey] || '').toLowerCase().trim();
      if (!term) return options;
      return options.filter((option) => {
        const label = (option.label || '').toLowerCase();
        return label.includes(term);
      });
    },
    /** Values of options currently visible after search filtering. */
    filteredOptionValues(chipKey) {
      return this.filteredOptions(chipKey).map((option) => option.value);
    },
    /** True if every visible option is checked in pending state. */
    areAllFilteredSelected(chipKey) {
      const values = this.filteredOptionValues(chipKey);
      if (!values.length) return false;
      const pending = this.pendingFilters[chipKey] || [];
      return values.every((value) => pending.includes(value));
    },
    /** True if some but not all visible options are checked (indeterminate select-all). */
    isPartiallyFilteredSelected(chipKey) {
      const values = this.filteredOptionValues(chipKey);
      if (!values.length) return false;
      const pending = this.pendingFilters[chipKey] || [];
      const selectedCount = values.filter((value) => pending.includes(value)).length;
      return selectedCount > 0 && selectedCount < values.length;
    },
    /** Checkbox icon for select-all row (all, partial, or none). */
    selectAllIcon(chipKey) {
      if (this.areAllFilteredSelected(chipKey)) {
        return 'mdi-checkbox-marked';
      }
      if (this.isPartiallyFilteredSelected(chipKey)) {
        return 'mdi-minus-box-outline';
      }
      return 'mdi-checkbox-blank-outline';
    },
    /** Label for select-all toggle (Select all / Unselect all). */
    selectAllLabel(chipKey) {
      return this.areAllFilteredSelected(chipKey) ? 'Unselect all' : 'Select all';
    },
    /** Toggles all visible options on or off in pending state. */
    toggleSelectAll(chipKey) {
      const values = this.filteredOptionValues(chipKey);
      const current = new Set(this.pendingFilters[chipKey] || []);
      if (this.areAllFilteredSelected(chipKey)) {
        values.forEach((value) => current.delete(value));
      } else {
        values.forEach((value) => current.add(value));
      }
      this.$set(this.pendingFilters, chipKey, [...current]);
    },
    /** True if a filter chip has at least one active applied value. */
    isActive(key) {
      return (this.activeFilters[key] || []).length > 0;
    },
    /** Count of active applied values shown on the chip badge. */
    activeCount(key) {
      return (this.activeFilters[key] || []).length;
    },
    /** True if a value is checked in pending (pre-apply) state. */
    isPendingSelected(key, value) {
      return (this.pendingFilters[key] || []).includes(value);
    },
    /** Toggles a single option in pending state before Apply. */
    togglePendingOption(key, value) {
      const current = [...(this.pendingFilters[key] || [])];
      const index = current.indexOf(value);
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(value);
      }
      this.$set(this.pendingFilters, key, current);
    },
    /** Clears all pending selections for a chip (does not apply). */
    clearPending(key) {
      this.$set(this.pendingFilters, key, []);
    },
    /** Commits pending selections to active filters and emits filter-change. */
    applyFilter(key) {
      const next = { ...this.activeFilters };
      const selected = this.pendingFilters[key] || [];
      const allValues = this.allOptionValues(key);
      const isAllSelected = allValues.length > 0
        && allValues.every((value) => selected.includes(value));

      if (!selected.length || isAllSelected) {
        delete next[key];
      } else {
        next[key] = [...selected];
      }
      this.$emit('filter-change', next);
      this.openMenuKey = null;
    },
  },
};
</script>
